import { useEffect } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { Col, FormFeedback, Input, Label, Row } from 'reactstrap';
import { useProfile } from '../../../../store/Auth/authHooks';
import {
  useCompanyManagementMyCompany,
  useFetchMyCompany,
} from '../../../../store/CompanyManagement/companyManagementHooks';
import { emailValidation } from '../../../../utility/functions/emailValidation';
import PhoneInput from '../../../shared/PhoneInput/PhoneInput';

type PropsTypes = {
  form: UseFormReturn<any, object>;
  isEdit?: boolean;
};

export const GeneralInformation = (props: PropsTypes) => {
  const profile = useProfile();
  const { t } = useTranslation();
  const myCompany = useCompanyManagementMyCompany();
  const fetchMyCompany = useFetchMyCompany();

  useEffect(() => {
    fetchMyCompany().finally();
  }, [profile.locationId]);

  const {
    getValues,
    control,
    formState: { errors },
  } = props.form;

  const companiesOptions = [{ value: myCompany.id, label: myCompany.name }];

  return (
    <div>
      <Row className="section-title">
        <Col md="8">
          <div>{t('GENERAL_INFORMATION_TITLE')}</div>
        </Col>
      </Row>
      <Row>
        <Col md="4" className="form-user-input">
          <Label for="fullName">
            <span>* </span>
            {t('FORM_FULL_NAME_LABEL')}
          </Label>
          <Controller
            name="fullName"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
              },
              pattern: {
                value: /^[0-9a-zA-Z _"'.\u0590-\u05FF\u200f\u200e]*$/,
                message: t('FORM_ERRORS_INVALID_USE_LETTERS_NUMBER'),
              },
              minLength: {
                value: 2,
                message: t('FORM_ERRORS_FIELD_TOO_SHORT'),
              },
            }}
            render={({ field }) => (
              <Input
                autoComplete="off"
                id="fullName"
                autoFocus
                type="text"
                className="text-input"
                placeholder={t('FORM_FULL_NAME_PLACEHOLDER')}
                invalid={errors.fullName && true}
                {...field}
              />
            )}
          />
          {errors.fullName ? (
            <FormFeedback className="form-input-error">
              {errors.fullName.message}
            </FormFeedback>
          ) : null}
        </Col>
        <Col md="4" className="form-user-input">
          <Label for="email">
            <span>* </span>
            {t('FORM_EMAIL_LABEL')}
          </Label>
          <Controller
            name="email"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
              },
              validate: {
                emailPattern: (e) =>
                  emailValidation(e, t('FORM_ERRORS_INVALID_EMAIL')),
              },
            }}
            render={({ field }) => (
              <Input
                disabled={props.isEdit}
                autoComplete="off"
                id="email"
                type="email"
                className="text-input"
                placeholder={t('FORM_EMAIL_PLACEHOLDER')}
                invalid={errors.email && true}
                {...field}
              />
            )}
          />
          {errors.email ? (
            <FormFeedback className="form-input-error">
              {errors.email.message}
            </FormFeedback>
          ) : null}
        </Col>
        <Col md="4" className="form-user-input">
          <Label for="job_title">{t('FORM_JOB_TITLE_LABEL')}</Label>
          <Controller
            name="job_title"
            control={control}
            rules={{
              required: {
                value: false,
                message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
              },
            }}
            render={({ field }) => (
              <Input
                autoComplete="off"
                id="job_title"
                type="text"
                className="text-input"
                placeholder={t('FORM_JOB_TITLE_PLACEHOLDER')}
                invalid={errors.job_title && true}
                {...field}
              />
            )}
          />
          {errors.job_title ? (
            <FormFeedback className="form-input-error">
              {errors.job_title.message}
            </FormFeedback>
          ) : null}
        </Col>
        <Col md="4" className="form-user-input">
          <Label for="company">{t('COMPANY')}</Label>
          <Controller
            control={control}
            rules={{
              required: {
                value: true,
                message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
              },
            }}
            defaultValue={companiesOptions.map((c) => c.value)}
            name="company"
            render={({ field: { onChange, value } }) => (
              <Select
                isDisabled={true}
                value={companiesOptions.filter((c) => value === c.value)}
                onChange={(val) => onChange(val?.value)}
                options={companiesOptions}
                placeholder={t('COMPANY')}
              />
            )}
          />
          {errors.company ? (
            <FormFeedback className="form-input-error">
              {errors.company.message}
            </FormFeedback>
          ) : null}
        </Col>
        <Col md="4" className="form-user-input">
          <PhoneInput
            required
            label={t('FORM_PHONE_NUMBER_LABEL')}
            placeholder={t('FORM_PHONE_NUMBER_LABEL')}
            controlName={'phone'}
            form={props.form}
            control={control}
          />
        </Col>
      </Row>
    </div>
  );
};
